import React, { useState, useEffect, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { baseUrl } from '../../config';
import {
  Tabs, Input, Spin, Empty, Tag, Tooltip, Select,
  Row, Col, Typography, Button, Collapse, Badge, Space, Statistic,
} from 'antd';
import {
  GlobalOutlined, EnvironmentOutlined, HomeOutlined,
  TeamOutlined, BankOutlined, SearchOutlined,
  RiseOutlined, PieChartOutlined, PrinterOutlined,
  FileExcelOutlined, OrderedListOutlined,
} from '@ant-design/icons';
import MapView from './MapView';

const { Panel } = Collapse;

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

// ── Colour palette for charts ────────────────────────────────────────────────
const PALETTE = [
  '#1d4ed8','#7c3aed','#db2777','#ea580c','#16a34a',
  '#0891b2','#d97706','#4f46e5','#0f766e','#9333ea',
  '#dc2626','#059669','#b45309','#2563eb','#7e22ce',
  '#c026d3','#0284c7','#65a30d','#e11d48','#0369a1',
];

// ── Treemap chart options factory ────────────────────────────────────────────
const treemapOpts = (title, valueLabel = 'members') => ({
  chart: { type: 'treemap', toolbar: { show: false }, fontFamily: 'Arial, sans-serif', animations: { enabled: true, speed: 600 } },
  title: { text: title, style: { fontSize: '13px', fontWeight: '700', color: '#7a0000' } },
  colors: PALETTE,
  plotOptions: { treemap: { distributed: true, enableShades: false } },
  dataLabels: {
    enabled: true,
    formatter: (text, op) => [text, op.value],
    style: { fontSize: '11px', fontFamily: 'Arial', colors: ['#fff'], fontWeight: '700' },
    offsetY: -4,
  },
  tooltip: { y: { formatter: v => `${v.toLocaleString()} ${valueLabel}` }, theme: 'dark' },
  legend: { show: false },
});

// ── Horizontal bar chart options factory ─────────────────────────────────────
const hbarOpts = (categories, color = '#1d4ed8') => ({
  chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Arial, sans-serif', animations: { enabled: true, speed: 600 } },
  colors: [color],
  plotOptions: { bar: { horizontal: true, barHeight: '60%', distributed: false, borderRadius: 4 } },
  dataLabels: { enabled: true, style: { fontSize: '11px', colors: ['#fff'] }, formatter: v => v.toLocaleString() },
  xaxis: { categories, labels: { style: { fontSize: '11px', colors: '#555' } } },
  yaxis: { labels: { style: { fontSize: '11px', colors: '#7a0000', fontWeight: '600' }, maxWidth: 150 } },
  grid: { borderColor: '#f0f0f0' },
  tooltip: { y: { formatter: v => `${v.toLocaleString()} members` }, theme: 'dark' },
  legend: { show: false },
  fill: { type: 'gradient', gradient: { shade: 'light', type: 'horizontal', gradientToColors: ['#7c3aed'], stops: [0, 100] } },
});

// ── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, subtitle, gradient, icon, small }) => (
  <div style={{
    background: gradient,
    borderRadius: 12,
    padding: small ? '14px 16px' : '18px 20px',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    boxShadow: '0 4px 18px rgba(0,0,0,0.18)',
    transition: 'transform 0.2s',
    cursor: 'default',
  }}
    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
  >
    <div style={{ fontSize: small ? 28 : 34, opacity: 0.9 }}>{icon}</div>
    <div>
      <div style={{ fontSize: small ? 22 : 28, fontWeight: 800, lineHeight: 1 }}>
        {value != null ? Number(value).toLocaleString('en-IN') : '—'}
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.9, marginTop: 2 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 10, opacity: 0.75, marginTop: 1 }}>{subtitle}</div>}
    </div>
  </div>
);

// ── Ranked table with inline progress bar ────────────────────────────────────
const RankTable = ({ rows, col1, col2, col3, maxCount, topN = 30, searchable }) => {
  const [q, setQ] = useState('');
  const filtered = useMemo(() =>
    q ? rows.filter(r => (r[col1] || '').toLowerCase().includes(q.toLowerCase())) : rows
  , [rows, q, col1]);
  const shown = filtered.slice(0, topN);

  return (
    <div>
      {searchable && (
        <Input
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          placeholder={`Search ${col1}…`}
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{ marginBottom: 10, maxWidth: 300 }}
          size="small"
        />
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#7a0000', color: '#fff' }}>
              <th style={{ padding: '7px 10px', width: 36, textAlign: 'center' }}>#</th>
              <th style={{ padding: '7px 10px', textAlign: 'left' }}>{col1.charAt(0).toUpperCase() + col1.slice(1)}</th>
              {col2 && <th style={{ padding: '7px 10px', textAlign: 'left' }}>{col2.charAt(0).toUpperCase() + col2.slice(1)}</th>}
              {col3 && <th style={{ padding: '7px 10px', textAlign: 'left' }}>{col3.charAt(0).toUpperCase() + col3.slice(1)}</th>}
              <th style={{ padding: '7px 10px', textAlign: 'right', minWidth: 50 }}>Count</th>
              <th style={{ padding: '7px 80px 7px 10px', textAlign: 'left', minWidth: 120 }}>Share</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => {
              const pct = maxCount > 0 ? Math.round((r.count / maxCount) * 100) : 0;
              const rank = filtered.indexOf(r) + 1;
              return (
                <tr key={i} style={{ background: i % 2 === 0 ? '#f7f9fc' : '#fff' }}>
                  <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-block', width: 22, height: 22, lineHeight: '22px',
                      borderRadius: '50%', textAlign: 'center', fontSize: 10, fontWeight: 700,
                      background: rank <= 3 ? ['#f59e0b','#94a3b8','#b45309'][rank-1] : '#e2e8f0',
                      color: rank <= 3 ? '#fff' : '#64748b'
                    }}>{rank}</span>
                  </td>
                  <td style={{ padding: '6px 10px', fontWeight: 600, color: '#7a0000' }}>{r[col1]}</td>
                  {col2 && <td style={{ padding: '6px 10px', color: '#64748b' }}>{r[col2] || '—'}</td>}
                  {col3 && <td style={{ padding: '6px 10px', color: '#64748b' }}>{r[col3] || '—'}</td>}
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700, color: '#1d4ed8' }}>
                    {Number(r.count).toLocaleString('en-IN')}
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ flex: 1, height: 8, background: '#e2e8f0', borderRadius: 4 }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg,#1d4ed8,#7c3aed)', borderRadius: 4 }} />
                      </div>
                      <span style={{ fontSize: 10, color: '#94a3b8', minWidth: 28 }}>{pct}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {shown.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#bbb' }}>No results</td></tr>
            )}
          </tbody>
        </table>
        {filtered.length > topN && (
          <div style={{ textAlign: 'center', padding: '8px 0', color: '#94a3b8', fontSize: 11 }}>
            Showing top {topN} of {filtered.length} results
          </div>
        )}
      </div>
    </div>
  );
};

// ── Section wrapper ──────────────────────────────────────────────────────────
const Section = ({ title, icon, children }) => (
  <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 18 }}>
    <div style={{ background: '#7a0000', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ color: '#93c5fd', fontSize: 13 }}>{icon}</span>
      <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>{title}</span>
    </div>
    <div style={{ padding: 16 }}>{children}</div>
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
export default function GeoReport() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [villages, setVillages] = useState([]);
  const [occStates, setOccStates] = useState([]);
  const [occDistricts, setOccDistricts] = useState([]);
  const [stateFilter, setStateFilter] = useState('all');

  // ── Village District List (new tab) ─────────────────────────────────────────
  const [vdList, setVdList]         = useState(null);
  const [vdLoading, setVdLoading]   = useState(false);
  const [vdSearch, setVdSearch]     = useState('');
  const [vdSearchInput, setVdSearchInput] = useState('');

  useEffect(() => {
    const h = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
    Promise.all([
      axios.get(`${baseUrl}/geo-report/summary`,       { headers: h }),
      axios.get(`${baseUrl}/geo-report/state-wise`,    { headers: h }),
      axios.get(`${baseUrl}/geo-report/district-wise`, { headers: h }),
      axios.get(`${baseUrl}/geo-report/tehsil-wise`,   { headers: h }),
      axios.get(`${baseUrl}/geo-report/village-wise`,  { headers: h }),
      axios.get(`${baseUrl}/geo-report/occupation-wise`, { headers: h }),
    ]).then(([s, st, d, t, v, o]) => {
      setSummary(s.data.result);
      setStates(st.data.result || []);
      setDistricts(d.data.result || []);
      setTehsils(t.data.result || []);
      setVillages(v.data.result || []);
      setOccStates(o.data.result?.states || []);
      setOccDistricts(o.data.result?.districts || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  // ── Fetch village-district list on demand ────────────────────────────────────
  const fetchVdList = (search = '') => {
    setVdLoading(true);
    const h = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    axios.get(`${baseUrl}/geo-report/village-district-list${params}`, { headers: h })
      .then(r => { if (r.data.status) setVdList(r.data); })
      .catch(console.error)
      .finally(() => setVdLoading(false));
  };

  // ── Export Village List to Excel ──────────────────────────────────────────────
  const exportVdExcel = () => {
    if (!vdList) return;
    const rows = [];
    let sno = 1;
    vdList.districts.forEach(d => {
      d.tehsils.forEach(t => {
        t.villages.forEach(v => {
          rows.push({
            'S.No':    sno++,
            'जिला':   d.district,
            'राज्य':  d.state || '',
            'तहसील':  t.tehsil,
            'गाँव':   v.village,
            'सदस्य':  v.members,
          });
        });
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'जिला-तहसील-गाँव सूची');
    XLSX.writeFile(wb, 'village_district_list.xlsx');
  };

  // ── Print village list ────────────────────────────────────────────────────────
  const printVdList = () => {
    if (!vdList) return;
    let html = `<html><head><meta charset="utf-8"><title>जिला-तहसील-गाँव सूची</title>
    <style>
      body { font-family: Arial, sans-serif; font-size: 13px; color: #111; padding: 24px; }
      h1 { font-size: 18px; text-align: center; color: #7a0000; margin-bottom: 4px; }
      .summary { text-align: center; color: #555; margin-bottom: 20px; font-size: 12px; }
      .district { margin-bottom: 18px; }
      .district-title { font-size: 15px; font-weight: 800; color: #7a0000; background: #e8f0fe;
        padding: 5px 10px; border-left: 5px solid #7a0000; margin-bottom: 6px; }
      .tehsil-title { font-size: 13px; font-weight: 700; color: #444; margin: 6px 0 3px 16px; }
      .village-grid { display: flex; flex-wrap: wrap; gap: 4px 16px; margin-left: 32px; margin-bottom: 4px; }
      .village-item { font-size: 12px; color: #333; min-width: 180px; }
      .village-count { color: #888; font-size: 11px; }
      @media print { @page { margin: 15mm; } }
    </style></head><body>
    <h1>जिला / तहसील / गाँव सूची</h1>
    <div class="summary">
      कुल जिले: ${vdList.total_districts} &nbsp;|&nbsp;
      कुल तहसील: ${vdList.total_tehsils} &nbsp;|&nbsp;
      कुल गाँव: ${vdList.total_villages} &nbsp;|&nbsp;
      कुल सदस्य: ${vdList.total_members}
    </div>`;

    vdList.districts.forEach(d => {
      html += `<div class="district">
        <div class="district-title">${d.district} &nbsp;
          <span style="font-size:11px;font-weight:400;color:#555">(${d.village_count} गाँव | ${d.member_count} सदस्य | ${d.state})</span>
        </div>`;
      d.tehsils.forEach(t => {
        html += `<div class="tehsil-title">📍 ${t.tehsil} &nbsp;
          <span style="font-size:11px;font-weight:400;color:#777">(${t.village_count} गाँव | ${t.member_count} सदस्य)</span>
        </div>
        <div class="village-grid">`;
        t.villages.forEach((v, i) => {
          html += `<div class="village-item">${i + 1}. ${v.village} <span class="village-count">(${v.members})</span></div>`;
        });
        html += `</div>`;
      });
      html += `</div>`;
    });

    html += `</body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 500);
  };

  // unique state options for filter
  const stateOptions = useMemo(() =>
    ['all', ...new Set(districts.map(d => d.state).filter(Boolean))], [districts]);

  const filteredDistricts = useMemo(() =>
    stateFilter === 'all' ? districts : districts.filter(d => d.state === stateFilter),
  [districts, stateFilter]);

  const filteredTehsils = useMemo(() =>
    stateFilter === 'all' ? tehsils : tehsils.filter(t => filteredDistricts.some(d => d.district === t.district)),
  [tehsils, filteredDistricts, stateFilter]);

  const maxState    = states[0]?.count || 1;
  const maxDistrict = filteredDistricts[0]?.count || 1;
  const maxTehsil   = filteredTehsils[0]?.count || 1;
  const maxVillage  = villages[0]?.count || 1;
  const maxOcc      = occStates[0]?.count || 1;

  const top20Villages = villages.slice(0, 20);
  const top15Tehsils  = filteredTehsils.slice(0, 15);
  const top10States   = states.slice(0, 10);
  const top10OccStates= occStates.slice(0, 10);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <Spin size="large" tip="Loading geographic data…" />
    </div>
  );

  return (
    <div style={{ background: '#f0f3f7', minHeight: '100vh' }}>

      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #7a0000 0%, #0f172a 100%)',
        padding: '20px 28px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
            🗺️
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 20, lineHeight: 1.2 }}>
              Geographic Member Distribution
            </div>
            <div style={{ color: '#93c5fd', fontSize: 12, marginTop: 2 }}>
              अखिल भारतीय राजपुरोहित समाज विकास संस्थान — सदस्य भौगोलिक वितरण
            </div>
          </div>
        </div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>
          📅 Live data — {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      </div>

      <div style={{ padding: '18px 20px' }}>

        {/* ── KPI Cards ───────────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
          <StatCard title="Total Members"      value={summary?.total_members}     subtitle="All registered"       gradient="linear-gradient(135deg,#1d4ed8,#3b82f6)" icon={<TeamOutlined />} />
          <StatCard title="Rajasthan Members"  value={summary?.rajasthan_members}  subtitle="Home state"           gradient="linear-gradient(135deg,#d97706,#f59e0b)" icon={<HomeOutlined />} />
          <StatCard title="Other States"       value={summary?.out_of_state_members} subtitle="Outside Rajasthan"  gradient="linear-gradient(135deg,#7c3aed,#8b5cf6)" icon={<GlobalOutlined />} />
          <StatCard title="States Covered"     value={summary?.states_covered}     subtitle="Across India"         gradient="linear-gradient(135deg,#16a34a,#22c55e)" icon={<RiseOutlined />} />
          <StatCard title="Districts"          value={summary?.districts_covered}  subtitle="Distinct districts"   gradient="linear-gradient(135deg,#0891b2,#06b6d4)" icon={<EnvironmentOutlined />} />
          <StatCard title="Tehsils"            value={summary?.tehsils_covered}    subtitle="Distinct tehsils"     gradient="linear-gradient(135deg,#4f46e5,#6366f1)" icon={<PieChartOutlined />} />
          <StatCard title="Villages"           value={summary?.villages_covered}   subtitle="Distinct villages"    gradient="linear-gradient(135deg,#db2777,#ec4899)" icon={<EnvironmentOutlined />} />
          <StatCard title="Work Locations"     value={summary?.with_occupation}    subtitle="Members with occ."    gradient="linear-gradient(135deg,#0f766e,#14b8a6)" icon={<BankOutlined />} />
        </div>

        {/* ── Main Tabs ───────────────────────────────────────────────────── */}
        <Tabs
          defaultActiveKey="0"
          size="middle"
          tabBarStyle={{ background: '#fff', padding: '0 16px', borderRadius: '10px 10px 0 0', border: '1px solid #e2e8f0', borderBottom: 'none', marginBottom: 0, fontWeight: 600 }}
          style={{ background: 'transparent' }}
        >

          {/* ══ Tab 0: Live Map ══════════════════════════════════════════ */}
          <TabPane tab={<span>🗺️ Live Map</span>} key="0">
            <div style={{ background: '#fff', borderRadius: '0 0 10px 10px', border: '1px solid #e2e8f0', borderTop: 'none', padding: 16, marginTop: 0 }}>
              <MapView />
            </div>
          </TabPane>

          {/* ══ Tab 1: India Overview ═════════════════════════════════════ */}
          <TabPane tab={<span><GlobalOutlined /> India Overview</span>} key="1">
            <div style={{ marginTop: 2 }}>
              {states.length === 0 ? (
                <Empty description="No state data available" style={{ padding: 60 }} />
              ) : (
                <>
                  <Section title="State-Wise Member Distribution (Treemap)" icon={<GlobalOutlined />}>
                    <ReactApexChart
                      type="treemap"
                      series={[{ data: states.map(s => ({ x: s.state, y: Number(s.count) })) }]}
                      options={treemapOpts('All States — Members Count')}
                      height={380}
                    />
                  </Section>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                    <Section title="Top 10 States — Bar Chart" icon={<RiseOutlined />}>
                      <ReactApexChart
                        type="bar"
                        series={[{ name: 'Members', data: top10States.map(s => Number(s.count)) }]}
                        options={hbarOpts(top10States.map(s => s.state), '#1d4ed8')}
                        height={320}
                      />
                    </Section>
                    <Section title="State Rankings" icon={<EnvironmentOutlined />}>
                      <RankTable rows={states} col1="state" maxCount={maxState} topN={20} searchable />
                    </Section>
                  </div>
                </>
              )}
            </div>
          </TabPane>

          {/* ══ Tab 2: Districts & Tehsils ════════════════════════════════ */}
          <TabPane tab={<span><EnvironmentOutlined /> Districts & Tehsils</span>} key="2">
            <div style={{ marginTop: 2 }}>
              {/* State filter */}
              <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                <Text strong style={{ fontSize: 12 }}>Filter by State:</Text>
                <Select value={stateFilter} onChange={setStateFilter} size="small" style={{ width: 200 }}>
                  {stateOptions.map(s => (
                    <Option key={s} value={s}>{s === 'all' ? '— All States —' : s}</Option>
                  ))}
                </Select>
              </div>

              {filteredDistricts.length === 0 ? (
                <Empty description="No district data" style={{ padding: 60 }} />
              ) : (
                <>
                  <Section title="District-Wise Member Distribution (Treemap)" icon={<EnvironmentOutlined />}>
                    <ReactApexChart
                      type="treemap"
                      series={[{ data: filteredDistricts.slice(0, 60).map(d => ({ x: d.district, y: Number(d.count) })) }]}
                      options={treemapOpts('Districts — Members Count')}
                      height={420}
                    />
                  </Section>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                    <Section title="District Rankings" icon={<EnvironmentOutlined />}>
                      <RankTable rows={filteredDistricts} col1="district" col2="state" maxCount={maxDistrict} topN={50} searchable />
                    </Section>
                    <Section title="Tehsil Rankings" icon={<EnvironmentOutlined />}>
                      <RankTable rows={filteredTehsils} col1="tehsil" col2="district" maxCount={maxTehsil} topN={50} searchable />
                    </Section>
                  </div>

                  {filteredTehsils.length > 0 && (
                    <Section title="Top 15 Tehsils — Bar Chart" icon={<RiseOutlined />}>
                      <ReactApexChart
                        type="bar"
                        series={[{ name: 'Members', data: top15Tehsils.map(t => Number(t.count)) }]}
                        options={hbarOpts(top15Tehsils.map(t => `${t.tehsil} (${t.district})`), '#7c3aed')}
                        height={380}
                      />
                    </Section>
                  )}
                </>
              )}
            </div>
          </TabPane>

          {/* ══ Tab 3: Villages ══════════════════════════════════════════ */}
          <TabPane tab={<span><HomeOutlined /> Villages</span>} key="3">
            <div style={{ marginTop: 2 }}>
              {villages.length === 0 ? (
                <Empty description="No village data" style={{ padding: 60 }} />
              ) : (
                <>
                  <Section title="Top 20 Villages — Members Count" icon={<HomeOutlined />}>
                    <ReactApexChart
                      type="bar"
                      series={[{ name: 'Members', data: top20Villages.map(v => Number(v.count)) }]}
                      options={hbarOpts(top20Villages.map(v => `${v.village}${v.tehsil ? ` · ${v.tehsil}` : ''}`), '#db2777')}
                      height={480}
                    />
                  </Section>

                  <Section title="Village-wise Rankings (All)" icon={<SearchOutlined />}>
                    <RankTable rows={villages} col1="village" col2="tehsil" col3="district" maxCount={maxVillage} topN={50} searchable />
                  </Section>
                </>
              )}
            </div>
          </TabPane>

          {/* ══ Tab 4: Work Locations ════════════════════════════════════ */}
          <TabPane tab={<span><BankOutlined /> Work Locations</span>} key="4">
            <div style={{ marginTop: 2 }}>
              {occStates.length === 0 ? (
                <Empty description="No occupation location data available" style={{ padding: 60 }} />
              ) : (
                <>
                  <Section title="Where Our Members Work — State-wise (Treemap)" icon={<BankOutlined />}>
                    <ReactApexChart
                      type="treemap"
                      series={[{ data: occStates.map(s => ({ x: s.state, y: Number(s.count) })) }]}
                      options={treemapOpts('Work Location — State Distribution', 'members working')}
                      height={340}
                    />
                  </Section>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                    <Section title="Top 10 Work States — Bar Chart" icon={<RiseOutlined />}>
                      <ReactApexChart
                        type="bar"
                        series={[{ name: 'Members', data: top10OccStates.map(s => Number(s.count)) }]}
                        options={hbarOpts(top10OccStates.map(s => s.state), '#0f766e')}
                        height={300}
                      />
                    </Section>
                    <Section title="Work State Rankings" icon={<BankOutlined />}>
                      <RankTable rows={occStates} col1="state" maxCount={maxOcc} topN={20} searchable />
                    </Section>
                  </div>

                  {occDistricts.length > 0 && (
                    <Section title="Work District Rankings" icon={<EnvironmentOutlined />}>
                      <RankTable rows={occDistricts} col1="district" col2="state" maxCount={occDistricts[0]?.count || 1} topN={50} searchable />
                    </Section>
                  )}
                </>
              )}
            </div>
          </TabPane>

          {/* ── NEW: Village District List Tab ── */}
          <TabPane
            tab={<span><OrderedListOutlined /> जिला/तहसील/गाँव सूची</span>}
            key="5"
          >
            <div style={{ padding: '8px 0' }}>
              {/* Controls */}
              <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
                <Col flex="1">
                  <Input
                    placeholder="जिला / तहसील / गाँव खोजें..."
                    prefix={<SearchOutlined />}
                    value={vdSearchInput}
                    onChange={e => setVdSearchInput(e.target.value)}
                    onPressEnter={() => { setVdSearch(vdSearchInput); fetchVdList(vdSearchInput); }}
                    allowClear
                    onClear={() => { setVdSearch(''); fetchVdList(''); }}
                  />
                </Col>
                <Col>
                  <Space>
                    <Button type="primary" icon={<SearchOutlined />}
                      onClick={() => { setVdSearch(vdSearchInput); fetchVdList(vdSearchInput); }}
                      style={{ background: '#7a0000', borderColor: '#7a0000' }}>
                      {vdList ? 'खोजें' : 'सूची लोड करें'}
                    </Button>
                    {vdList && <>
                      <Button icon={<PrinterOutlined />} onClick={printVdList}>Print</Button>
                      <Button icon={<FileExcelOutlined />} onClick={exportVdExcel}
                        style={{ borderColor: '#52c41a', color: '#52c41a' }}>Excel</Button>
                    </>}
                  </Space>
                </Col>
              </Row>

              {/* Summary stats */}
              {vdList && (
                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                  {[
                    { label: 'कुल जिले',  value: vdList.total_districts, color: '#1d4ed8' },
                    { label: 'कुल तहसील', value: vdList.total_tehsils,   color: '#7c3aed' },
                    { label: 'कुल गाँव',  value: vdList.total_villages,  color: '#16a34a' },
                    { label: 'कुल सदस्य', value: vdList.total_members,   color: '#ea580c' },
                  ].map(s => (
                    <Col xs={12} sm={6} key={s.label}>
                      <div style={{ textAlign: 'center', background: '#f8faff', border: `2px solid ${s.color}20`,
                        borderRadius: 10, padding: '10px 0' }}>
                        <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>
                          {s.value.toLocaleString('en-IN')}
                        </div>
                        <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{s.label}</div>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}

              {/* Loading */}
              {vdLoading && <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" tip="लोड हो रहा है..." /></div>}

              {/* No data yet */}
              {!vdList && !vdLoading && (
                <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>
                  <OrderedListOutlined style={{ fontSize: 48, marginBottom: 12, display: 'block' }} />
                  <div style={{ fontSize: 15 }}>जिला/तहसील/गाँव की पूरी सूची देखने के लिए <b>"सूची लोड करें"</b> बटन दबाएं</div>
                </div>
              )}

              {/* District → Tehsil → Village accordion */}
              {vdList && !vdLoading && (
                <Collapse accordion={false} bordered={false} style={{ background: 'transparent' }}>
                  {(vdList.districts || []).map((d, di) => (
                    <Panel
                      key={di}
                      style={{ marginBottom: 8, borderRadius: 8, border: '1px solid #dde3ed', background: '#fff' }}
                      header={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontWeight: 800, fontSize: 14, color: '#7a0000' }}>{d.district}</span>
                          <Tag color="blue">{d.village_count} गाँव</Tag>
                          <Tag color="green">{d.member_count} सदस्य</Tag>
                          {d.state && <Tag color="default" style={{ fontSize: 11 }}>{d.state}</Tag>}
                        </div>
                      }
                    >
                      {d.tehsils.map((t, ti) => (
                        <div key={ti} style={{ marginBottom: 12 }}>
                          {/* Tehsil header */}
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            background: '#f0f4ff', borderRadius: 6, padding: '5px 10px', marginBottom: 6,
                          }}>
                            <span style={{ fontWeight: 700, fontSize: 13, color: '#7a0000' }}>📍 {t.tehsil}</span>
                            <Badge count={t.village_count} style={{ background: '#722ed1' }} overflowCount={999} />
                            <span style={{ fontSize: 11, color: '#888' }}>{t.member_count} सदस्य</span>
                          </div>
                          {/* Village grid */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 0', paddingLeft: 12 }}>
                            {t.villages.map((v, vi) => (
                              <div key={vi} style={{
                                width: '33.33%', fontSize: 12, padding: '2px 8px',
                                color: '#333', borderBottom: '1px dotted #eee',
                              }}>
                                <span style={{ color: '#aaa', marginRight: 5, fontSize: 11 }}>{vi + 1}.</span>
                                {v.village}
                                <span style={{ color: '#1677ff', fontSize: 11, marginLeft: 4 }}>({v.members})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </Panel>
                  ))}
                </Collapse>
              )}
            </div>
          </TabPane>

        </Tabs>
      </div>
    </div>
  );
}
